import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { RefineThemes } from '@refinedev/antd';
import { theme } from 'antd';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  antdTheme: any;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themeState, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;
    return savedTheme || 'system';
  });

  const [antdTheme, setAntdTheme] = useState(RefineThemes.Orange);

  useEffect(() => {
    localStorage.setItem('theme', themeState);

    const baseTheme = {
      ...RefineThemes.Orange,
      token: {
        ...RefineThemes.Orange.token,
        fontFamily: 'Poppins, sans-serif',
        fontSize: 18,
        siderWidth: 500,
        siderCollapsedWidth: 80,
        menuItemPaddingInline: 16,
      },
    };

    if (themeState === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setAntdTheme(
        systemTheme === 'dark'
          ? { ...baseTheme, algorithm: theme.darkAlgorithm }
          : baseTheme
      );
    } else if (themeState === 'dark') {
      setAntdTheme({ ...baseTheme, algorithm: theme.darkAlgorithm });
    } else {
      setAntdTheme(baseTheme);
    }
  }, [themeState]);

  useEffect(() => {
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (themeState === 'system') {
        const baseTheme = {
          ...RefineThemes.Orange,
          token: {
            ...RefineThemes.Orange.token,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            siderWidth: 260,
            siderCollapsedWidth: 80,
            menuItemPaddingInline: 16,
          },
        };
        setAntdTheme(e.matches ? { ...baseTheme, algorithm: theme.darkAlgorithm } : baseTheme);
      }
    };

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeState]);

  return (
    <ThemeContext.Provider value={{ theme: themeState, setTheme: setThemeState, antdTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};